import { LitElement, html, css, svg } from "lit-element";
import { store } from "../../../acc-boot/acc-boot.js";
import { translation } from "../../../acc-mixins/extend.js";
import { periodFromDate } from "../../../acc-mixins/dates.js";
import { track } from "../../../../../common/util/tealium.tsx";
import { Command } from "../../../acc-es-data/src/command";

/**
 * @export
 * @class elementBaseClass
 * @mixes translation
 */
const elementBaseClass = class base extends translation(LitElement) {
  static get properties() {
    return {
      /**
       * Most components need the affAbo, so presuming their not processing
       * models or they need a passed value, we can use this to hit all the
       * stops by using a custom setter
       * @attr
       */
      affAbo: {
        type: String,
        reflect: true,
      },
      /**
       * Used for most API calls, defines the current MMMMYY period for a given
       * result set
       * @attr
       */
      period: {
        type: String,
        reflect: true,
      },
    };
  }

  /**
   * Define a custom setter for affAbo to make life easier
   * @param {string} value the previous set
   */
  set affAbo(value) {
    const oldValue = this._affAbo;
    if (!value) {
      this._affAbo = `${store.getState().user.aff}-${store.getState().user.abo}`;
    } else {
      this._affAbo = value;
    }
    this.requestUpdate("affAbo", oldValue);
  }

  get affAbo() {
    return this._affAbo;
  }

  constructor() {
    super();

    // private reference cache
    this.__dateRefs = {
      today: new Date(),
      todayAsPeriod: periodFromDate(new Date()),
    };

    this.__command = new Command(store.getState().configuration, store.getState().user, store.getState().user);

    this.__mountDataReady = false;

    this.affAbo = "";
    this.period = this.__dateRefs.todayAsPeriod;
  }

  connectedCallback() {
    performance.mark(`FCP:${this.tagName}:START`);
    super.connectedCallback();
  }

  firstUpdated() {
    super.firstUpdated();
    performance.mark(`FCP:${this.tagName}:END`);

    // this is failsafe case in case we're simply too fast on the cache
    // return, resulting in the DOM not ready but the data being already set
    if (this.__mountDataReady) {
      this._removeLoadingAnimation(".loading");
    }
    performance.measure(`FCP:${this.tagName}`, `FCP:${this.tagName}:START`, `FCP:${this.tagName}:END`);

    // this is a case of requiring specific :lang pseudo selector access to
    // handle language specific CSS needs, such as Japan
    this.setAttribute("lang", this.locale);
  }

  /**
   * super.mount() within your mount implementation
   */
  mount() {
    if (!this.__mountDataReady) {
      this.__mountDataReady = true;
    }
    this._removeLoadingAnimation(".loading");
  }

  /**
   * remove loading class from blocks
   * @param {string} blockTarget string to use with querySelector; should be a css path
   */
  _removeLoadingAnimation(blockTarget) {
    const elements = [...this.shadowRoot.querySelectorAll(blockTarget)];
    elements.forEach((element) => element.classList.remove("loading"));
    elements.forEach((element) => element.classList.add("waitingOnPeriodUpdate"));
  }

  /**
   * add loading class to blocks
   * @param {string} blockTarget string to use with querySelector; should be a css path
   */
  _startLoadingAnimation(blockTarget) {
    const elements = [...this.shadowRoot.querySelectorAll(blockTarget)];
    elements.forEach((element) => element.classList.remove("waitingOnPeriodUpdate"));
    elements.forEach((element) => element.classList.add("loading"));
  }

  /**
   * Lifecycle method that we use to listen for attribute changes to period
   * @param {*} name
   * @param {*} oldVal
   * @param {*} newVal
   */
  async attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    switch (name) {
      case "affabo":
      case "period": {
        // we check oldVal against newVal as a failsafe in this case; this could
        // also be lifted into a more generalized case for certain required
        // actions
        if (oldVal !== newVal) {
          this._startLoadingAnimation(".waitingOnPeriodUpdate");
          // mount() reloads our data view based on changed condition
          await this.mount();
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Adding click tracking support found in ED LOS
   * TODO: Move up for a more global application
   * @param {*} detail
   * @param {String} appScope
   */
  static trackClick(detail, appScope = "LOS_diamond") {
    track("link", "click_action", "", appScope, detail, "click");
  }
};

export { elementBaseClass, html, css, svg, store };
